import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import {
  hasTraits,
  traitsOf,
  traitValueLabel,
  TRAIT_ABOUT,
  TRAIT_KEYS,
  TRAIT_LABEL,
  TRAIT_PROMPT,
  TRAIT_VALUES,
  TRAIT_VALUE_ABOUT,
} from '../../core/traits';
import { speciesList } from '../../core/region';
import { t, tParts } from '../../strings';
import { icon } from '../components/icons';
import { traitIcon } from '../components/trait-icons';
import { openModeMenu } from '../components/mode-menu';
import { focusCodes, type Focus } from '../../core/quiz/focus';
import type { Species, TraitKey } from '../../types';

// The traits guide: what the five axes mean, value by value.
//
// Birdle asks what a bird's nest is and offers "platform", "pendant" and
// "scrape", and the species page prints the same words under the photograph —
// both assume a learner already knows the difference between a pendant and a
// dome. This is where they find out. Reached from the (i) on the Birdle header,
// from the foot of every trait dropdown on the board, and from each pill on a
// species page, which deep-links to its own value with `?axis=&value=`.
//
// Each value carries the birds that hold it, out of the packs actually selected:
// a few of the commonest as examples, one per family so "cavity" is not four
// woodpeckers, and the full list behind a disclosure. A definition says what a
// scrape is; "Killdeer, Mallard, Glaucous-winged Gull" says it faster.
//
// And each value is a round: "Study these 12" under the cavity nesters opens the
// same mode menu the Library's lenses do, over a `trait:nest=cavity` focus. This
// is where trait filtering lives, rather than as chips in the Library — five
// axes of several values each is thirty-odd chips, which no scrolling row holds,
// and this page already lays every value out with its birds under it.

ensureStyle(
  'traits-css',
  `
  .traitguide { padding:18px var(--pad) 28px; display:flex; flex-direction:column; gap:18px; }
  .traitguide .back { align-self:flex-start; margin-left:-6px; }
  .traitguide h1 { font-size:30px; margin:0; }
  .traitguide .intro { line-height:1.55; margin:-6px 0 0; }
  /* Jump to an axis. Buttons that scroll rather than anchors: the router owns
     the hash, so #nest would be a route to nowhere. */
  .traitguide .jump { display:flex; flex-wrap:wrap; gap:6px; }
  .traitguide .jump .chip { padding:5px 12px; font-size:13px; cursor:pointer; }
  .traitguide .axis { display:flex; flex-direction:column; gap:10px;
    scroll-margin-top:12px; }
  .traitguide .axis h2 { font-size:22px; margin:6px 0 0; }
  .traitguide .axis .ask { font-style:italic; color:var(--ink-soft); margin:-8px 0 0;
    font-size:13px; }
  .traitguide .axis .about { line-height:1.5; margin:0; }
  .traitguide .vals { display:flex; flex-direction:column; }
  .traitguide .val { display:flex; gap:12px; padding:12px 10px;
    border-bottom:1.5px solid var(--hairline); border-radius:var(--radius-hand);
    scroll-margin:80px; }
  .traitguide .val:last-child { border-bottom:none; }
  /* The value a species pill linked to. */
  .traitguide .val.on { background:var(--sunk); border-bottom-color:transparent; }
  .traitguide .val .glyph { flex:none; width:28px; padding-top:1px; color:var(--ink-soft);
    display:flex; justify-content:center; }
  .traitguide .val .body { flex:1; min-width:0; display:flex; flex-direction:column; gap:3px; }
  .traitguide .val .nm { font-family:var(--font-display); font-size:17px; line-height:1.2; }
  .traitguide .val .def { line-height:1.45; font-size:14px; }
  .traitguide .val .eg { font-size:12.5px; color:var(--ink-soft); line-height:1.5; }
  .traitguide .val .eg a, .traitguide .val details a { color:var(--ink); }
  .traitguide .val details { font-size:12.5px; line-height:1.6; color:var(--ink-soft); }
  .traitguide .val summary { cursor:pointer; width:max-content; letter-spacing:.06em;
    text-transform:uppercase; font-size:10.5px; }
  .traitguide .val .study { align-self:flex-start; gap:6px; margin:3px 0 0 -8px;
    padding:4px 8px; font-size:13px; color:var(--ink-soft); }
  .traitguide .val .study:hover, .traitguide .val .study:focus-visible { color:var(--ink); }
`,
);

/** How many example birds a value names before the full list. */
const EXAMPLES = 4;

/** The birds in the selected packs holding each value, commonest first. */
function holders(): Record<TraitKey, Map<string, Species[]>> {
  const out = {} as Record<TraitKey, Map<string, Species[]>>;
  for (const key of TRAIT_KEYS) {
    out[key] = new Map(TRAIT_VALUES[key].map((v) => [v, []]));
  }
  const birds = speciesList()
    .filter(hasTraits)
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || a.common.localeCompare(b.common));
  for (const sp of birds) {
    const traits = traitsOf(sp);
    for (const key of TRAIT_KEYS) {
      for (const value of traits[key]) out[key].get(value)?.push(sp);
    }
  }
  return out;
}

/** The commonest bird of each family, up to `EXAMPLES` of them. */
function examples(birds: Species[]): Species[] {
  const seen = new Set<string>();
  const out: Species[] = [];
  for (const sp of birds) {
    const family = sp.family ?? sp.code;
    if (seen.has(family)) continue;
    seen.add(family);
    out.push(sp);
    if (out.length >= EXAMPLES) break;
  }
  return out;
}

/** A bird's name as a link to its page. */
function birdLink(sp: Species, ctx: ViewContext): HTMLElement {
  const link = el('a', { href: `#/species/${sp.code}` }, sp.common);
  link.addEventListener('click', (e) => {
    e.preventDefault();
    ctx.navigate(`/species/${sp.code}`);
  });
  return link;
}

/** Names as links, comma-separated. */
function birdLinks(birds: Species[], ctx: ViewContext): (HTMLElement | string)[] {
  return birds.flatMap((sp, i) => (i ? [', ', birdLink(sp, ctx)] : [birdLink(sp, ctx)]));
}

/**
 * "Study these N" for one value, or nothing when none of its birds can be asked
 * about. Counted through `focusCodes`, not off the list above it: the list shows
 * every bird in the selected packs, and a round skips the ones set aside.
 */
function studyButton(focus: Focus, ctx: ViewContext): HTMLElement | null {
  const codes = focusCodes(focus);
  if (!codes.length) return null;
  const button = el(
    'button.study.btn.btn-plain',
    { type: 'button' },
    icon('binoculars', { size: 15 }),
    t('traits.guide.study', { n: codes.length }),
  ) as HTMLButtonElement;
  button.addEventListener('click', () =>
    openModeMenu(button, { focus, codes, navigate: (path) => ctx.navigate(path) }),
  );
  return button;
}

/** Nodes and text as one node, to fill a single argument of a message. */
function fragment(parts: (HTMLElement | string)[]): DocumentFragment {
  const out = document.createDocumentFragment();
  out.append(...parts);
  return out;
}

export const traitsView: View = (root, ctx) => {
  const byValue = holders();
  const sections = new Map<TraitKey, HTMLElement>();
  let target: HTMLElement | null = null;

  const wantAxis = TRAIT_KEYS.find((k) => k === ctx.params.axis);
  const wantValue = wantAxis && ctx.params.value;

  const jump = el(
    'div.jump',
    {},
    ...TRAIT_KEYS.map((key) =>
      el(
        'button.chip',
        {
          type: 'button',
          onclick: () => sections.get(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        },
        TRAIT_LABEL[key],
      ),
    ),
  );

  const axes = TRAIT_KEYS.map((key) => {
    const rows = TRAIT_VALUES[key].map((value) => {
      const birds = byValue[key].get(value) ?? [];
      const shown = examples(birds);
      const row = el(
        'div.val',
        {},
        el('span.glyph', {}, traitIcon(key, value, { size: 22 })),
        el(
          'div.body',
          {},
          el('span.nm', {}, traitValueLabel(key, value)),
          el('span.def', {}, TRAIT_VALUE_ABOUT[key][value]),
          shown.length
            ? el(
                'span.eg',
                {},
                ...tParts('traits.guide.examples', {
                  birds: fragment(birdLinks(shown, ctx)),
                }),
              )
            : el('span.eg', {}, t('traits.guide.noBirds')),
          // The whole list only where it says more than the examples did.
          birds.length > shown.length
            ? el(
                'details',
                {},
                el('summary', {}, t('traits.guide.allBirds', { n: birds.length })),
                ...birdLinks(birds, ctx),
              )
            : null,
          studyButton({ kind: 'trait', trait: { axis: key, value } }, ctx),
        ),
      );
      if (key === wantAxis && value === wantValue) {
        row.classList.add('on');
        target = row;
      }
      return row;
    });
    const section = el(
      'section.axis',
      {},
      el('h2', {}, TRAIT_LABEL[key]),
      el('p.ask', {}, TRAIT_PROMPT[key]),
      el('p.about', {}, TRAIT_ABOUT[key]),
      el('div.vals', {}, ...rows),
    );
    sections.set(key, section);
    return section;
  });

  root.append(
    el(
      'div.traitguide',
      {},
      el(
        'button.back.btn.btn-plain',
        { onclick: () => ctx.back('/birdle'), 'aria-label': t('common.back') },
        icon('arrow-left', { size: 22 }),
      ),
      el('h1', {}, t('traits.guide.title')),
      el('p.intro.meta', {}, t('traits.guide.intro')),
      jump,
      ...axes,
    ),
  );

  // `root` is already in the document, so this measures real layout — and it
  // runs after the router's own scroll to the top.
  const landOn = target ?? (wantAxis ? sections.get(wantAxis) : null);
  landOn?.scrollIntoView({ block: target ? 'center' : 'start' });
};
